import { LoadingOutlined } from '@ant-design/icons';
import { Widget } from '@typeform/embed-react';
import { Spin } from 'antd';
import { useSession } from 'next-auth/react';
import { ReactElement, useMemo, useState } from 'react';
import styled from 'styled-components';

import FrontImage from '../../assets/images/LandingPage/frontImg.png';
import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import { Center } from '../../components/_atoms/Center';
import QuotesPageContent from '../../components/_organisms/QuotesPageContent';
import { NextPageWithLayout } from '../../components/layouts';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import useAccessBadge from '../../hooks/useAccessBadge';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import useRouterMatch from '../../hooks/useRouterMatch';
import { ById } from '../../services/backendService/types';

const PricingPage: NextPageWithLayout = () => {
  return <QuotesPageContent />;
};

const CustomHeader = () => {
  const session = useSession();
  const { tenantId: activeTenantId } = useRouterMatch();
  const { accessBadge, isFetching } = useAccessBadge();

  const isDesktop = useBreakPoint(900);

  const [isDone, setIsDone] = useState(false);

  const hiddenFields = useMemo(() => {
    const fields: ById<string> = {};
    if (accessBadge?.organization?.id) {
      fields['organization_id'] = accessBadge.organization.id;
    }
    if (accessBadge?.user?.email) {
      fields['email'] = accessBadge.user.email;
    }
    if (accessBadge?.user?.id) {
      fields['user_id'] = accessBadge.user.id;
    }
    return fields;
  }, [accessBadge]);

  const memoised = useMemo(() => {
    return (
      <Widget
        hidden={hiddenFields}
        id={'SD54oAwS'}
        medium="curipod-admin-embed"
        height={500}
        width={isDesktop ? 500 : 300}
        onSubmit={() => setIsDone(true)}
        onEndingButtonClick={() => setIsDone(true)}
        autoFocus={true}
      />
    );
  }, [hiddenFields, isDesktop]);

  return (
    <HeaderContainer>
      <h1 style={{ width: '100%', textAlign: 'center', fontSize: 36 }}>
        {isDone ? 'Thank you!' : 'Get a free trial of Curipod’s school plan!'}
      </h1>
      {!isDone && (
        <p style={{ width: '100%', textAlign: 'center' }}>
          {`Fill out this form to get a free trial of Curipod’s school plan!`}
        </p>
      )}
      <EmbeddedContainer>
        {/* Only render the form when the user is logged in and the data is fetched. */}
        {session.status === 'authenticated' && !activeTenantId && (
          <>
            <Center style={{ width: '100%', height: 200, padding: '10px' }}>
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 16, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            </Center>
          </>
        )}
        {session.status === 'authenticated' && isFetching && !accessBadge && (
          <>
            <Center style={{ width: '100%', height: 200, padding: '10px' }}>
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 16, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            </Center>
          </>
        )}
        {session.status === 'authenticated' && accessBadge && !isDone && <>{memoised}</>}
        {/* Just render the form immediately when the user is not logged in. */}
        {session.status !== 'authenticated' && !isDone && <>{memoised}</>}
        {isDone && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p
              style={{
                width: '100%',
                maxWidth: isDesktop ? '500px' : '300px',
                textAlign: 'center',
                fontSize: 18,
                lineHeight: '1.5',
              }}
            >
              Thank you for your interest in Curipod’s school plan! We know you are busy
              but in order for us to extend your trial for 4 weeks, we need a quick (10-15
              min meeting) where we will set up a free trial for you.
            </p>
            <a
              href="https://forms.gle/uKmX2wE7SzQex4i5A"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'underline !important' }}
            >
              <Button type="primary">Book now</Button>
            </a>
          </div>
        )}
        <p style={{ marginTop: 40, width: '100%', textAlign: 'center' }}>
          If the form is not showing after a few seconds,{' '}
          <A
            style={{ textDecoration: 'underline !important' }}
            href={`https://9uq31r57i3q.typeform.com/to/SD54oAwS#organization_id=${hiddenFields['organization_id']}&email=${hiddenFields['email']}&user_id=${hiddenFields['user_id']}`}
            target="_blank"
            rel="noreferrer"
          >
            Click here
          </A>
        </p>
      </EmbeddedContainer>
    </HeaderContainer>
  );
};

const A = styled.a`
  &&& {
    text-decoration: underline !important;
  }
`;

const EmbeddedContainer = styled.div`
  display: grid;
  justify-content: center;
  align-content: center;
  height: 600px;
  width: 100%;
  padding: 40px 0;
  background: ${theme.colors.background};
`;

const HeaderContainer = styled.div`
  /* height: 500px; */
  width: 100%;
  background: ${theme.colors.background};
  padding-top: 40px;
`;

PricingPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <LandingPageLayout
      title="Quotes"
      subTitle="How to get a quote"
      primaryActionText="Sign up - It's free"
      image={FrontImage}
      hideBlobs={false}
      customHeader={<CustomHeader />}
      disableHeadline={true}
    >
      {page}
    </LandingPageLayout>
  );
};

export default PricingPage;
